import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Label,
  FormGroup,
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import TableContainer from "../../Components/Common/TableContainer";
import { useNavigate } from "react-router-dom";
import { Toast, ToastBody, ToastHeader } from "reactstrap";
import { useLocation } from "react-router-dom";
import RetourModal from "./RetourModal";
import ValidationChoiceModal from "./ValidationChoiceModal";
import RetourAnnulationModal from "./RetourAnnulationModal";

interface Client {
  rowid: string;
  nom: string;
  address?: string;
  email?: string;
  phone?: string;
  outstanding_limit?: string;
  town?: string | null;
  zip?: string | null;
}

interface Order {
  rowid: string;
  ref: string;
  date_commande: string;
  name: string;
  total_ht: string;
  total_ttc: string;
  fk_statut: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  billed?: string;
  openExpeditions?: number;
  draftExpeditions?: number;
  validatedExpeditions?: number;
}

interface Invoice {
  invoiceid: string;
  ref: string;
  ref_client: string | null;
  date_facture: number;
  total_ht: string;
  total_ttc: string;
}

interface OrderDetails {
  id: string;
  ref: string;
  client: string;
  clientid: string;
  total_ht: string;
  total_ttc: string;
  date_commande: number;
  date_livraison: string;
  status: string;
  type: string;
  commandepdf: string;
  resteapayer: number;
  invoices: Invoice[];
  invoicepdf: string;
  lines: Array<{
    id: string;
    product_label: string;
    subprice: string;
    qty: string;
    tva_tx: string;
    total_ht: string;
    total_tva: string;
    total_ttc: string;
    remise_percent: string;
    product_type: string;
    fk_product: string;
    brand_id: string;
    brand: string;
    rowid: string;
    ref: string;
    qty_expedition?: string;
    id_expeditiondet?: string;
  }>;
  thirdparty: {
    name: string;
    address?: string;
    email?: string;
    phone?: string;
    rowid: string;
  };
  expeditions?: {
    [key: string]: string;
  };
  array_options: {
    options_validation?: number;
  };
  openExpeditions?: number;
}

interface SmartPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  countPerPage: number;
  onPageChange: (page: number) => void;
  loading: boolean;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  countPerPage,
  onPageChange,
  loading,
}) => {
  const maxVisiblePages = 5;
  const halfVisible = Math.floor(maxVisiblePages / 2);
  const [pageInput, setPageInput] = useState(currentPage.toString());

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  let startPage = Math.max(1, currentPage - halfVisible);
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handlePageJump = () => {
    const newPage = parseInt(pageInput);
    if (!isNaN(newPage)) {
      const clampedPage = Math.max(1, Math.min(totalPages, newPage));
      onPageChange(clampedPage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handlePageJump();
    }
  };

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="text-muted">
        Affichage <strong>{countPerPage}</strong>
      </div>

      <Pagination className="mb-0">
        <PaginationItem disabled={currentPage === 1 || loading}>
          <PaginationLink first onClick={() => onPageChange(1)} />
        </PaginationItem>
        <PaginationItem disabled={currentPage === 1 || loading}>
          <PaginationLink
            previous
            onClick={() => onPageChange(currentPage - 1)}
          />
        </PaginationItem>

        {startPage > 1 && (
          <>
            <PaginationItem disabled={loading}>
              <PaginationLink onClick={() => onPageChange(1)}>1</PaginationLink>
            </PaginationItem>
            {startPage > 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
          </>
        )}

        {pages.map((page) => (
          <PaginationItem
            active={page === currentPage}
            key={page}
            disabled={loading}
          >
            <PaginationLink onClick={() => onPageChange(page)}>
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem disabled={loading}>
              <PaginationLink onClick={() => onPageChange(totalPages)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem disabled={currentPage === totalPages || loading}>
          <PaginationLink next onClick={() => onPageChange(currentPage + 1)} />
        </PaginationItem>
        <PaginationItem disabled={currentPage === totalPages || loading}>
          <PaginationLink last onClick={() => onPageChange(totalPages)} />
        </PaginationItem>
      </Pagination>

      <div className="d-flex align-items-center gap-1">
        <Input
          type="number"
          min="1"
          max={totalPages}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ width: "60px" }}
          className="text-center"
          bsSize="sm"
          disabled={loading}
        />
        <Button
          color="primary"
          size="sm"
          onClick={handlePageJump}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" /> : "Aller"}
        </Button>
      </div>
    </div>
  );
};

const OrdersPage = () => {
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchRef, setSearchRef] = useState("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [countPerPage, setCountPerPage] = useState(0);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [fetchedClients, setFetchedClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientFetchError, setClientFetchError] = useState<string | null>(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [enCompte, setEnCompte] = useState<boolean>(false);
  const [comptoir, setComptoir] = useState<boolean>(false);
  const [clientName, setClientName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedLines, setEditedLines] = useState<OrderDetails["lines"]>([]);
  const [deletedLineIds, setDeletedLineIds] = useState<string[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("info");
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [retourOrderData, setRetourOrderData] = useState<any>(null);
  const [orderPdf, setOrderPdf] = useState<{
    invoicepdf: string;
    commandepdf: string;
  } | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfType, setPdfType] = useState<"invoice" | "order">("invoice");
  const [retourModalOpen, setRetourModalOpen] = useState(false);
  const [validationChoiceModalOpen, setValidationChoiceModalOpen] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validatedWithExpeditions, setValidatedWithExpeditions] = useState(
    location.state?.validatedWithExpeditions || false
  );
  const [annulationModalOpen, setAnnulationModalOpen] = useState(false);
  const [annulationOrderData, setAnnulationOrderData] = useState<any>(null);

  const handleOpenAnnulationModal = async (order: Order) => {
    try {
      if (order.fk_statut !== "2") {
        setToastMessage("Cette commande n'est pas au statut 'En cours de livraison'");
        setToastColor("warning");
        setToastOpen(true);
        return;
      }

      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "orderdetails",
          entity: user.entity,
          ref: order.ref,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.success && response.data) {
        if (response.data.expeditions) {
          response.data.lines = response.data.lines.map((line: any) => ({
            ...line,
            qty_expedition: response.data.expeditions[line.rowid] || "0",
            id_expeditiondet: line.id_expeditiondet || null,
          }));
        }

        const orderDataForAnnulation = {
          success: true,
          message: "Commande chargée",
          data: response.data,
          client: {
            clientid: response.data.fk_soc || "",
            nom: response.data.thirdparty?.name || order.name,
          },
        };

        setAnnulationModalOpen(true);
        setAnnulationOrderData(orderDataForAnnulation);
      } else {
        setToastMessage("Impossible de charger les détails de la commande");
        setToastColor("danger");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error fetching order details for annulation:", error);
      setToastMessage("Erreur lors du chargement des détails");
      setToastColor("danger");
      setToastOpen(true);
    }
  };

  const handleAnnulationModalClose = (success: boolean, message?: string) => {
    setAnnulationModalOpen(false);
    
    if (success && message) {
      setToastMessage(message);
      setToastColor("success");
      setToastOpen(true);
      
      fetchOrders();
      
      if (modalOpen) {
        setModalOpen(false);
      }
    }
  };

  const tableStyles = {
    actionCell: {
      width: "80px",
      textAlign: "center" as const,
      verticalAlign: "middle",
    },
    quantityCell: {
      textAlign: "center" as const,
      verticalAlign: "middle",
    },
    quantityInput: {
      width: "70px",
      margin: "0 auto",
      textAlign: "center" as const,
    },
    deleteButton: {
      display: "block",
      margin: "0 auto",
      width: "30px",
      height: "30px",
      padding: "0",
      alignItems: "center" as const,
      justifyContent: "center" as const,
    },
  };

  const fetchOrders = async () => {
    setLoading(true);

    try {
      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "orders",
          entity: user.entity,
          page: page,
          socid: clientId,
          search_ref: searchRef,
          search_validated_with_expeditions: validatedWithExpeditions ? 1 : 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const ordersWithClientInfo = response.data.map((order: any) => ({
        ...order,
        client_email: order.email || "",
        client_phone: order.phone || "",
        client_address: order.address || "",
        openExpeditions: order.openExpeditions || 0,
        draftExpeditions: order.draftExpeditions || 0,
        validatedExpeditions: order.validatedExpeditions || 0,
      }));

      setOrders(ordersWithClientInfo || []);
      setTotalCount(response.nbtotalofrecords);
      setTotalPages(response.totalpages);
      setCountPerPage(response.count);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (orderRef: string) => {
    setDetailsLoading(true);

    try {
      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "orderdetails",
          entity: user.entity,
          ref: orderRef,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.data) {
        const clientid =
          response.clientid || response.fk_soc || response.data.clientid || "";

        const linesWithExpedition = response.data.lines.map((line: any) => ({
          ...line,
          qty_expedition: response.data.expeditions?.[line.rowid] || "0",
          id_expeditiondet: line.id_expeditiondet || null,
        }));

        setOrderDetails({
          ...response.data,
          clientid: clientid,
          invoices: response.invoices || [],
          expeditions: response.data.expeditions || {},
          openExpeditions: response.data.openExpeditions || 0,
          lines: linesWithExpedition,
        });
        setOrderPdf({
          invoicepdf: response.invoicepdf,
          commandepdf: response.orderpdf || response.data.orderpdf || "",
        });
        setEditedLines([...linesWithExpedition]);
        setDeletedLineIds([]);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleAddOrderToCart = () => {
    if (!orderDetails) return;

    const savedCart = localStorage.getItem("cart");
    let currentCart = savedCart
      ? JSON.parse(savedCart)
      : { client: "", items: [] };

    if (currentCart.items.length > 0) {
      setToastMessage(
        "Le panier n'est pas vide. Voulez-vous le vider et ajouter cette commande?"
      );
      setToastColor("warning");
      setToastOpen(true);

      setConfirmAction(() => {
        return () => {
          const newCartItems = orderDetails.lines.map((line: any) => ({
            cartItemId: `order-${orderDetails.ref}-${line.id}-${Date.now()}`,
            ref: line.product_ref,
            lineId: line.id,
            articleName: line.product_label,
            brand: line.brand || "",
            price_ttc: parseFloat(line.total_ttc) / parseInt(line.qty),
            quantity: parseInt(line.qty),
            price_min: 0,
            product: { thumb: null },
            order_id: orderDetails.id,
          }));

          const newCart = {
            client: orderDetails.thirdparty.name,
            items: newCartItems,
            clientInfo: {
              rowid: orderDetails.clientid || "",
              name: orderDetails.thirdparty.name,
              address: orderDetails.thirdparty.address,
              email: orderDetails.thirdparty.email,
              phone: orderDetails.thirdparty.phone,
            },
            order_id: orderDetails.id,
            socid: orderDetails.clientid || "",
          };

          localStorage.setItem("cart", JSON.stringify(newCart));
          window.dispatchEvent(new Event("cart-updated"));
          setModalOpen(false);
          localStorage.setItem("openCartOnVendeur", "true");
          navigate("/vendeur");
        };
      });

      return;
    }

    const newCartItems = orderDetails.lines.map((line: any) => ({
      cartItemId: `order-${orderDetails.ref}-${line.id}-${Date.now()}`,
      id: line.fk_product,
      ref: line.product_ref,
      lineId: line.id,
      articleName: line.product_label,
      brand: line.brand || "",
      price_ttc: parseFloat(line.total_ttc) / parseInt(line.qty),
      quantity: parseInt(line.qty),
      price_min: 0,
      product: { thumb: null },
      order_id: orderDetails.id,
    }));

    const newCart = {
      client: orderDetails.thirdparty.name,
      items: newCartItems,
      clientInfo: {
        rowid: orderDetails.clientid || "",
        name: orderDetails.thirdparty.name,
        address: orderDetails.thirdparty.address,
        email: orderDetails.thirdparty.email,
        phone: orderDetails.thirdparty.phone,
      },
      order_id: orderDetails.id,
      socid: orderDetails.clientid || "",
    };

    localStorage.setItem("cart", JSON.stringify(newCart));
    window.dispatchEvent(new Event("cart-updated"));
    setModalOpen(false);
    localStorage.setItem("openCartOnVendeur", "true");
    navigate("/vendeur");
  };

  const handleCancelDeleteOrder = async (orderRef: string, orderId: string) => {
    try {
      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "cancelorder",
          entity: user.entity,
          order_id: orderId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.success) {
        setToastMessage(response.message || "Commande traitée avec succès");
        setToastColor("success");
        setToastOpen(true);

        fetchOrders();
        

        if (modalOpen && orderDetails?.ref === orderRef) {
          setModalOpen(false);
        }
      } else {
        setToastMessage(response.message || "Erreur lors du traitement");
        setToastColor("danger");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error canceling/deleting order:", error);
      setToastMessage("Erreur lors du traitement de la commande");
      setToastColor("danger");
      setToastOpen(true);
    }
  };

  const handleEditOrder = () => {
    if (orderDetails) {
      setEditedLines([...orderDetails.lines]);
      setDeletedLineIds([]);
      setIsEditing(true);
    }
  };

  const handleQuantityChange = (index: number, value: string) => {
    const newLines = [...editedLines];
    const qty = parseInt(value) || 0;
    newLines[index].qty = qty.toString();

    const price = parseFloat(newLines[index].subprice) || 0;
    const tvaRate = parseFloat(newLines[index].tva_tx) || 0;
    newLines[index].total_ht = (price * qty).toFixed(2);
    newLines[index].total_tva = ((price * qty * tvaRate) / 100).toFixed(2);
    newLines[index].total_ttc = (price * qty * (1 + tvaRate / 100)).toFixed(2);

    setEditedLines(newLines);
  };

  const handleToggleDeleteLine = (lineId: string) => {
    setDeletedLineIds((prev) =>
      prev.includes(lineId)
        ? prev.filter((id) => id !== lineId)
        : [...prev, lineId]
    );
  };

  const handleValidateChanges = async () => {
    if (!orderDetails || !user) return;

    try {
      if (deletedLineIds.length > 0) {
        const deleteResponse = await axios.post(
          "https://api.apapiecesauto.fr/apiV2/",
          {
            action: "deleteorderline",
            entity: user.entity,
            id: orderDetails.id,
            line_ids: deletedLineIds,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
      }

      if (editedLines.length > 0) {
        const remainingLines = editedLines.filter(
          (line) => !deletedLineIds.includes(line.id)
        );

        if (remainingLines.length > 0) {
          const orderData = {
            action: "updateorder",
            entity: user.entity,
            id: orderDetails.id,
            lines: remainingLines.map((line) => ({
              id: line.id,
              unitprice: parseFloat(line.subprice) || 0,
              qty: parseInt(line.qty) || 0,
            })),
          };

          const updateResponse = await axios.post(
            "https://api.apapiecesauto.fr/apiV2/",
            orderData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }
          );
        }
      }

      if (deletedLineIds.length > 0 || editedLines.length > 0) {
        setToastMessage("Modifications validées avec succès.");
        setToastColor("success");
        setToastOpen(true);

        await fetchOrderDetails(orderDetails.ref);
      }
    } catch (error) {
      console.error("Error validating changes:", error);
      setToastMessage("Erreur lors de la validation des modifications.");
      setToastColor("danger");
      setToastOpen(true);
    }

    setIsEditing(false);
    setDeletedLineIds([]);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (orderDetails) {
      setEditedLines([...orderDetails.lines]);
      setDeletedLineIds([]);
    }
  };

  const getStatusBadge = (statusCode: string) => {
    switch (statusCode) {
      case "-1":
        return { label: "Annulée", color: "danger" };
      case "0":
        return { label: "Brouillon", color: "warning" };
      case "1":
        return { label: "Validée", color: "success" };
      case "2":
        return { label: "En cours", color: "info" };
      case "3":
        return { label: "Livrée", color: "success" };
      case "4":
        return { label: "Processed", color: "primary" };
      default:
        return { label: "Unknown", color: "secondary" };
    }
  };

  const handleViewOrder = async (orderId: string) => {
    const order = orders.find((o) => o.rowid === orderId);
    if (order) {
      await fetchOrderDetails(order.ref);
      setModalOpen(true);
    }
  };

  const searchAndOpenOrderById = async (orderId: string) => {
    setDetailsLoading(true);

    try {
      const directResponse = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "orderdetails",
          entity: user.entity,
          id: orderId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (directResponse.data) {
        const orderDetails = directResponse.data;

        setOrderDetails(orderDetails);
        setDetailsLoading(false);
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching order details by ID:", error);
      setToastMessage("Commande non trouvée.");
      setToastColor("danger");
      setToastOpen(true);
      setDetailsLoading(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleValidatedWithExpeditionsChange = (checked: boolean) => {
    setValidatedWithExpeditions(checked);
    if (checked) {
      setClientId(null);
      setClientName("");
      setClientSearchTerm("");
      setEnCompte(false);
      setComptoir(false);
    }
  };

  const handleOpenRetourModal = async (order: Order) => {
    try {
      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "orderdetails",
          entity: user.entity,
          ref: order.ref,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.success && response.data) {
        const orderDataForRetour = {
          success: true,
          message: "Commande chargée",
          data: response.data,
          client: {
            clientid: response.data.fk_soc || "",
            nom: response.data.thirdparty?.name || order.name,
          },
        };

        setRetourModalOpen(true);
        setRetourOrderData(orderDataForRetour);
      } else {
        setToastMessage("Impossible de charger les détails de la commande");
        setToastColor("danger");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error fetching order details for retour:", error);
      setToastMessage("Erreur lors du chargement des détails");
      setToastColor("danger");
      setToastOpen(true);
    }
  };

  const handleValidateExpedition = async () => {
    if (!orderDetails) return;

    try {
      setValidateLoading(true);
      const response: any = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "validPreparation",
          entity: user.entity,
          orderid: orderDetails.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.success) {
        setToastMessage("Expédition validée avec succès");
        setToastColor("success");
        setToastOpen(true);

        await fetchOrderDetails(orderDetails.ref);
      } else {
        setToastMessage(response.message || 'Erreur lors de la validation');
        setToastColor("danger");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error validating expedition:", error);
      setToastMessage("Erreur lors de la validation de l'expédition");
      setToastColor("danger");
      setToastOpen(true);
    } finally {
      setValidateLoading(false);
    }
  };

  const handleValidateWithFournisseurs = async (
    success: boolean,
    message: string
  ) => {
    setValidationChoiceModalOpen(false);

    if (success) {
      setToastMessage(
        message || "Expédition validée avec succès avec commandes fournisseurs"
      );
      setToastColor("success");
      setToastOpen(true);

      if (orderDetails) {
        await fetchOrderDetails(orderDetails.ref);
      }
    } else {
      setToastMessage(
        message || "Erreur lors de la validation avec fournisseurs"
      );
      setToastColor("danger");
      setToastOpen(true);
    }
  };

  const handleClientSelect = (client: Client) => {
    setClientId(client.rowid);
    setClientName(client.nom);
    setClientSearchTerm(client.nom);
    setFetchedClients([]);
    setClientDropdownOpen(false);
    setPage(1);
    setValidatedWithExpeditions(false);
  };

  const handleEnCompteChange = (checked: boolean) => {
    setEnCompte(checked);
    if (checked) {
      setComptoir(false);
      setClientId(null);
      setClientName("");
      setClientSearchTerm("");
      setFetchedClients([]);

      setValidatedWithExpeditions(true);
    }
    setFetchedClients([]);
    setClientDropdownOpen(false);
  };

  const handleComptoirChange = (checked: boolean) => {
    setComptoir(checked);
    if (checked) {
      setEnCompte(false);
      setClientId("74057");
      setClientName("Client Comptoir");
      setClientSearchTerm("Client Comptoir");
      setFetchedClients([]);
      setClientDropdownOpen(false);
      setValidatedWithExpeditions(true);
    } else {
      setClientId(null);
      setClientName("");
      setClientSearchTerm("");
    }
  };

  const fetchClients = async () => {
    if (clientSearchTerm.trim().length < 3 || comptoir) {
      setFetchedClients([]);
      return;
    }

    setLoadingClients(true);
    setClientFetchError(null);

    try {
      const response = await axios.post(
        "https://api.apapiecesauto.fr/apiV2/",
        {
          action: "clients",
          entity: user.entity,
          search: clientSearchTerm,
          encompte: enCompte,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const clients: Client[] = response.data || [];
      setFetchedClients(clients);
    } catch (error) {
      setClientFetchError("Erreur lors de la récupération des clients.");
    } finally {
      setLoadingClients(false);
    }
  };

  const clearClientFilter = () => {
    setClientId(null);
    setClientName("");
    setClientSearchTerm("");
    setFetchedClients([]);
    setEnCompte(false);
    setComptoir(false);
    setPage(1);
  };

  const clearValidatedWithExpeditionsFilter = () => {
    setValidatedWithExpeditions(false);
  };

  const columns = [
    {
      header: "Référence",
      accessorKey: "ref",
      cell: (cell: any) => (
        <span className="fw-semibold">{cell.getValue()}</span>
      ),
    },
    {
      header: "Date Commande",
      accessorKey: "date_commande",
      cell: (cell: any) => (
        <span>{new Date(cell.getValue()).toLocaleDateString()}</span>
      ),
    },
    {
      header: "Client",
      accessorKey: "name",
      cell: (cell: any) => <span>{cell.getValue()}</span>,
    },
    {
      header: "Total HT",
      accessorKey: "total_ht",
      cell: (cell: any) => (
        <span>{parseFloat(cell.getValue()).toFixed(2)} €</span>
      ),
    },
    {
      header: "Total TTC",
      accessorKey: "total_ttc",
      cell: (cell: any) => (
        <span>{parseFloat(cell.getValue()).toFixed(2)} €</span>
      ),
    },
    {
      header: "Statut",
      accessorKey: "fk_statut",
      cell: (cell: any) => {
        const status = getStatusBadge(cell.getValue());
        const row = cell.row.original;
        const waitingForValidation = row.extrafield_validation === "1" || row.extrafield_validation === 1;
        const isBilled = row.billed === "1" || row.billed === 1;
        
        return (
          <div className="d-flex align-items-center justify-content-center gap-1">
            <Badge color={status.color}>
              {status.label}
            </Badge>

            {waitingForValidation && (
              <i
                className="ri-error-warning-line text-warning"
                title="Expédition à confirmer"
                style={{ fontSize: "14px" }}
              />
            )}
            {isBilled && (
              <i
                className="ri-currency-fill text-success"
                title="Facturée"
                style={{ fontSize: "14px" }}
              />
            )}
          </div>
        );
      },
      meta: {
        headerClass: "text-center",
        cellClass: "text-center",
        style: {
          textAlign: "center",
        },
      },
    },
    {
      header: "Action",
      accessorKey: "rowid",
      cell: (cell: any) => {
        const orderRow = cell.row.original;
        const isBilled = orderRow.billed === "1";
        const status = orderRow.fk_statut;
        const isCanceled = status === "-1";

        const shouldShowCancelForStatus2 = !isBilled && status === "2" && !isCanceled;
        const shouldShowCancelDelete = !isBilled && (status === "0" || status === "1" || status === "3");

        return (
          <div className="d-flex justify-content-end gap-2">
            {shouldShowCancelForStatus2 && (
              <Button
                color="warning"
                size="sm"
                onClick={() => handleOpenAnnulationModal(orderRow)}
                disabled={loading}
                className="px-2"
              >
                <i className="ri-close-circle-line me-1"></i>
                Annuler
              </Button>
            )}

            {shouldShowCancelDelete && (
              <Button
                color="danger"
                size="sm"
                onClick={() => {
                  setToastMessage(
                    `Voulez-vous vraiment ${
                      status === "0" ? "supprimer" : "annuler"
                    } cette commande ?`
                  );
                  setToastColor("warning");
                  setToastOpen(true);
                  setConfirmAction(
                    () => () =>
                      handleCancelDeleteOrder(orderRow.ref, orderRow.rowid)
                  );
                }}
                disabled={loading}
                className="px-2"
              >
                <i
                  className={`ri-${
                    status === "0" ? "delete-bin-line" : "close-circle-line"
                  }`}
                ></i>{" "}
                {status === "0" ? "Supprimer" : "Annuler"}
              </Button>
            )}
            
            {!isBilled && !isCanceled && status === "3" && (
              <Button
                color="warning"
                size="sm"
                onClick={() => handleOpenRetourModal(orderRow)}
                disabled={loading}
                className="px-2"
              >
                <i className="ri-arrow-go-back-line"></i> Retour
              </Button>
            )}
            
            <Button
              color="primary"
              size="sm"
              onClick={() => handleViewOrder(orderRow.rowid)}
              disabled={loading}
              className="px-2"
            >
              <i className="ri-eye-line"></i> Voir
            </Button>
          </div>
        );
      },
      meta: {
        headerClass: "text-center",
        cellClass: "text-center",
        style: {
          textAlign: "center",
        },
      },
    },
  ];

  useEffect(() => {
    if (location.state?.openOrderModal && location.state?.orderId) {
      searchAndOpenOrderById(location.state.orderId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    fetchOrders();
  }, [page, clientId, searchRef, user.entity, validatedWithExpeditions]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (clientSearchTerm.trim().length >= 3 && !comptoir) {
        fetchClients();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [clientSearchTerm, enCompte, comptoir]);

  useEffect(() => {
    if (location.state?.validatedWithExpeditions) {
      setValidatedWithExpeditions(true);
      navigate(location.pathname, { replace: true, state: {} });
      setClientId(null);
      setClientName("");
      setClientSearchTerm("");
      setEnCompte(false);
      setComptoir(false);
    }
  }, [location.state, navigate, location.pathname]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between mb-3">
                  <h4 className="card-title mb-0">Liste des Commandes</h4>
                  <div className="text-muted">
                    Total: <strong>{totalCount}</strong> commande(s)
                  </div>
                </div>

                <form onSubmit={handleSearch} className="mb-4">
                  <Row className="g-3 align-items-end">
                    <Col md={3}>
                      <FormGroup className="mb-0">
                        <Label for="reference" className="form-label fw-semibold small text-muted mb-1">
                          N° COMMANDE
                        </Label>
                        <InputGroup>
                          <InputGroupText className="bg-light border-end-0">
                            <i className="ri-barcode-line"></i>
                          </InputGroupText>
                          <Input
                            id="reference"
                            type="text"
                            placeholder="Rechercher par N° de commande..."
                            value={searchRef}
                            onChange={(e) => setSearchRef(e.target.value)}
                            className="border-start-0"
                            style={{ height: "42px" }}
                          />
                        </InputGroup>
                      </FormGroup>
                    </Col>

                    <Col md={4} style={{ marginLeft: "20px" }}>
                      <FormGroup className="mb-0">
                        <Label className="form-label fw-semibold small text-muted mb-1">
                          CLIENT
                        </Label>

                        <div className="d-flex gap-2 align-items-center">
                          <div className="d-flex flex-column gap-1 me-2">
                            <FormGroup check className="mb-0">
                              <Input
                                type="checkbox"
                                id="enCompte"
                                checked={enCompte}
                                onChange={(e) =>
                                  handleEnCompteChange(e.target.checked)
                                }
                                className="form-check-input"
                                disabled={validatedWithExpeditions}
                              />
                              <Label
                                for="enCompte"
                                check
                                className="form-check-label small fw-medium"
                              >
                                En Compte
                              </Label>
                            </FormGroup>

                            <FormGroup check className="mb-0">
                              <Input
                                type="checkbox"
                                id="comptoir"
                                checked={comptoir}
                                onChange={(e) =>
                                  handleComptoirChange(e.target.checked)
                                }
                                className="form-check-input"
                                disabled={validatedWithExpeditions}
                              />
                              <Label
                                for="comptoir"
                                check
                                className="form-check-label small fw-medium"
                              >
                                Comptoir
                              </Label>
                            </FormGroup>
                          </div>

                          <div className="position-relative flex-grow-1">
                            <InputGroup>
                              <InputGroupText className="bg-light border-end-0">
                                <i className="ri-user-line"></i>
                              </InputGroupText>
                              <Input
                                type="text"
                                placeholder={
                                  comptoir
                                    ? "Client Comptoir"
                                    : "Rechercher un client..."
                                }
                                value={
                                  comptoir
                                    ? "Client Comptoir"
                                    : clientSearchTerm
                                }
                                onChange={(e) => {
                                  if (!comptoir && !validatedWithExpeditions) {
                                    setClientSearchTerm(e.target.value);
                                    if (e.target.value.length >= 3) {
                                      setClientDropdownOpen(true);
                                    } else {
                                      setClientDropdownOpen(false);
                                    }
                                  }
                                }}
                                onFocus={() => {
                                  if (
                                    !comptoir &&
                                    !validatedWithExpeditions &&
                                    clientSearchTerm.length >= 3
                                  ) {
                                    setClientDropdownOpen(true);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(
                                    () => setClientDropdownOpen(false),
                                    200
                                  );
                                }}
                                disabled={comptoir || validatedWithExpeditions}
                                className="border-start-0"
                                style={{ height: "42px" }}
                              />
                              {clientName && !validatedWithExpeditions && (
                                <Button
                                  color="light"
                                  onClick={clearClientFilter}
                                  className="border-0 text-muted"
                                  style={{ height: "42px" }}
                                  type="button"
                                >
                                  <i className="ri-close-line"></i>
                                </Button>
                              )}
                            </InputGroup>

                            {clientDropdownOpen &&
                              !comptoir &&
                              !validatedWithExpeditions &&
                              fetchedClients.length > 0 && (
                                <div
                                  className="position-absolute w-100 border shadow-lg bg-white mt-1 rounded"
                                  style={{
                                    zIndex: 1000,
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    top: "100%",
                                    left: 0,
                                  }}
                                >
                                  <div className="p-2">
                                    {loadingClients && (
                                      <div className="text-center py-2">
                                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                        <span className="text-muted">
                                          Recherche en cours...
                                        </span>
                                      </div>
                                    )}

                                    {clientFetchError && (
                                      <div className="alert alert-danger small mb-2 py-2">
                                        <i className="ri-error-warning-line me-1"></i>
                                        {clientFetchError}
                                      </div>
                                    )}

                                    {fetchedClients.length === 0 &&
                                      clientSearchTerm.length >= 3 &&
                                      !loadingClients && (
                                        <div className="text-center text-muted py-3">
                                          <i className="ri-user-search-line d-block mb-1"></i>
                                          Aucun client trouvé
                                        </div>
                                      )}

                                    {fetchedClients.map((client) => (
                                      <div
                                        key={client.rowid}
                                        onClick={() => {
                                          handleClientSelect(client);
                                          setClientDropdownOpen(false);
                                        }}
                                        className="py-2 px-3 border-bottom cursor-pointer"
                                        style={{
                                          whiteSpace: "normal",
                                          cursor: "pointer",
                                          transition: "background-color 0.2s",
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "#f8f9fa")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "transparent")
                                        }
                                      >
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1">
                                            <div className="fw-semibold text-primary mb-1">
                                              {client.nom}
                                            </div>
                                            {client.phone && (
                                              <div className="small text-muted mb-1">
                                                <i className="ri-phone-line me-1"></i>
                                                {client.phone}
                                              </div>
                                            )}
                                            {client.email && (
                                              <div className="small text-muted text-truncate">
                                                <i className="ri-mail-line me-1"></i>
                                                {client.email}
                                              </div>
                                            )}
                                          </div>
                                          <Badge
                                            color="light"
                                            className="ms-2 text-muted small"
                                          >
                                            {client.rowid}
                                          </Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      </FormGroup>
                    </Col>

                    <Col md={2}>
                      <FormGroup className="mb-0">
                        <Label className="form-label fw-semibold small text-muted mb-1">
                          FILTRES SPÉCIAUX
                        </Label>
                        <div className="border rounded p-2 bg-light">
                          <FormGroup check className="mb-0">
                            <Input
                              type="checkbox"
                              id="validatedWithExpeditions"
                              checked={validatedWithExpeditions}
                              onChange={(e) =>
                                handleValidatedWithExpeditionsChange(
                                  e.target.checked
                                )
                              }
                              className="form-check-input"
                              disabled={!!clientId || enCompte || comptoir}
                            />
                            <Label for="validatedWithExpeditions" check className="form-check-label fw-medium text-primary">
                              Attente de confirmation
                            </Label>
                            {validatedWithExpeditions && (
                              <Button
                                color="link"
                                size="sm"
                                onClick={clearValidatedWithExpeditionsFilter}
                                className="float-end p-0"
                                title="Effacer ce filtre"
                              >
                                <small>
                                  <i className="ri-close-line"></i>
                                </small>
                              </Button>
                            )}
                          </FormGroup>
                        </div>
                      </FormGroup>
                    </Col>

                    <Col md={2} className="ms-auto">
                      <FormGroup className="mb-0">
                        <Label className="form-label fw-semibold small text-muted mb-1 d-block">
                          &nbsp;
                        </Label>
                        <Button
                          type="submit"
                          color="primary"
                          className="w-100"
                          style={{ height: "42px" }}
                          disabled={loading}
                        >
                          {loading ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="ri-search-line align-bottom me-1"></i>
                              Rechercher
                            </>
                          )}
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                </form>

                {(clientName || validatedWithExpeditions) && (
                  <div className="alert alert-info mb-3 py-2">
                    <small>
                      <i className="ri-filter-line me-1"></i>
                      <strong>Filtres actifs:</strong>
                      {clientName && (
                        <Badge color="primary" className="ms-2">
                          Client: {clientName}
                          <Button
                            color="link"
                            size="sm"
                            onClick={clearClientFilter}
                            className="p-0 ms-1 text-white"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Badge>
                      )}
                      {validatedWithExpeditions && (
                        <Badge color="warning" className="ms-2">
                          En attente de confirmation
                          <Button
                            color="link"
                            size="sm"
                            onClick={clearValidatedWithExpeditionsFilter}
                            className="p-0 ms-1 text-dark"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Badge>
                      )}
                    </small>
                  </div>
                )}

                {loading && orders.length === 0 ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                    <div className="mt-2">Chargement des commandes...</div>
                  </div>
                ) : (
                  <>
                    {orders.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="ri-file-search-line display-5 text-muted"></i>
                        <h5 className="mt-3">Aucune commande trouvée</h5>
                        <p className="text-muted">
                          {searchRef || clientId || validatedWithExpeditions
                            ? "Essayez avec d'autres critères de recherche"
                            : "Il n'y a aucune commande à afficher"}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="position-relative">
                          {loading && (
                            <div
                              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 10,
                              }}
                            >
                              <div className="text-center">
                                <Spinner color="primary" />
                                <div className="mt-2">Chargement...</div>
                              </div>
                            </div>
                          )}

                          <TableContainer
                            columns={columns}
                            data={orders}
                            isGlobalFilter={false}
                            customPageSize={100}
                            divClass="table-responsive"
                            tableClass="align-middle table-nowrap"
                            theadClass="table-light text-muted"
                          />
                        </div>

                        {orders.length > 0 && (
                          <SmartPagination
                            currentPage={page}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            countPerPage={countPerPage}
                            onPageChange={handlePageChange}
                            loading={loading}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal de choix de validation */}
      {orderDetails && (
        <ValidationChoiceModal
          isOpen={validationChoiceModalOpen}
          toggle={() => setValidationChoiceModalOpen(false)}
          orderDetails={orderDetails}
          user={user}
          onValidateNormal={handleValidateExpedition}
          onValidateFournisseur={handleValidateWithFournisseurs}
          loading={validateLoading}
        />
      )}

      <Modal
        isOpen={modalOpen}
        toggle={() => {
          setModalOpen(false);
          setIsEditing(false);
        }}
        size="xl"
      >
        <ModalHeader
          toggle={() => {
            setModalOpen(false);
            setIsEditing(false);
          }}
          className="d-flex justify-content-between align-items-center"
        >
          <div>
            Commande {orderDetails?.ref}
            {orderDetails && (
              <Badge
                color={getStatusBadge(orderDetails.status).color}
                className="ms-2"
              >
                {getStatusBadge(orderDetails.status).label}
              </Badge>
            )}
          </div>
        </ModalHeader>
        <ModalBody>
          {detailsLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <div className="mt-2">Chargement des détails...</div>
            </div>
          ) : orderDetails ? (
            <div className="order-details-container">
              <Row className="mb-4">
                <Col md={6}>
                  <div className="p-3 border rounded mb-3">
                    <h6>Informations client</h6>
                    <div className="mb-2">
                      <strong>Client:</strong> {orderDetails.thirdparty.name}
                    </div>
                    {orderDetails.thirdparty.email && (
                      <div className="mb-2">
                        <strong>Email:</strong> {orderDetails.thirdparty.email}
                      </div>
                    )}
                    {orderDetails.thirdparty.phone && (
                      <div className="mb-2">
                        <strong>Téléphone:</strong>{" "}
                        {orderDetails.thirdparty.phone}
                      </div>
                    )}
                    {orderDetails.thirdparty.address && (
                      <div className="mb-2">
                        <strong>Adresse:</strong>{" "}
                        {orderDetails.thirdparty.address}
                      </div>
                    )}
                    <div>
                      <strong>Date commande:</strong>{" "}
                      {new Date(
                        orderDetails.date_commande * 1000
                      ).toLocaleDateString()}
                    </div>

                    {orderDetails?.invoices &&
                      orderDetails.invoices.length > 0 &&
                      orderDetails.invoices[0]?.ref && (
                        <div className="mb-2">
                          <strong>Facture :</strong>{" "}
                          {orderDetails.invoices[0].ref}
                        </div>
                      )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 border rounded mb-3">
                    <h6>Résumé financier</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total HT:</span>
                      <span>
                        {parseFloat(orderDetails.total_ht).toFixed(2)} €
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total TTC:</span>
                      <span>
                        {parseFloat(orderDetails.total_ttc).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Référence</th>
                      <th>Produit</th>
                      <th className="text-end">Qt commandée</th>
                      {(orderDetails?.openExpeditions || 0) > 0 && (
                        <th className="text-end">Qt préparée</th>
                      )}
                      <th className="text-end">Prix unitaire</th>
                      <th className="text-end">Remise</th>
                      <th className="text-end">Total HT</th>
                      <th className="text-end">Total TTC</th>
                      {isEditing && (
                        <th style={tableStyles.actionCell}>Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(isEditing ? editedLines : orderDetails?.lines || []).map(
                      (line, index) => {
                        const lineId = line.rowid || line.id;
                        const expeditionQuantity =
                          orderDetails?.expeditions?.[lineId] || "0";
                        const qtyToPrepare = parseInt(expeditionQuantity);
                        const hasAllExpedition =
                          qtyToPrepare == parseInt(line.qty);

                        return (
                          <tr
                            key={index}
                            style={
                              isEditing && deletedLineIds.includes(line.id)
                                ? { backgroundColor: "#FEF2F2" }
                                : {}
                            }
                          >
                            <td>{line.ref}</td>
                            <td style={{ maxWidth: "400px" }}>
                              {line.product_label || ""}
                            </td>

                            <td
                              className="text-end"
                              style={tableStyles.quantityCell}
                            >
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  value={line.qty}
                                  onChange={(e) =>
                                    handleQuantityChange(index, e.target.value)
                                  }
                                  style={tableStyles.quantityInput}
                                  className="text-end"
                                />
                              ) : (
                                line.qty
                              )}
                            </td>

                            {(orderDetails?.openExpeditions || 0) > 0 && (
                              <td className="text-end">
                                <Badge
                                  color={
                                    hasAllExpedition ? "success" : "danger"
                                  }
                                  pill
                                  className="fw-bold"
                                >
                                  {qtyToPrepare}
                                </Badge>
                              </td>
                            )}

                            <td className="text-end">
                              {parseFloat(line.subprice).toFixed(2)} €
                            </td>
                            <td style={{ width: "60px", textAlign: "right" }}>
                              {line.remise_percent} %
                            </td>
                            <td className="text-end">
                              {parseFloat(line.total_ht).toFixed(2)} €
                            </td>
                            <td className="text-end">
                              {parseFloat(line.total_ttc).toFixed(2)} €
                            </td>
                            {isEditing && (
                              <td style={tableStyles.actionCell}>
                                <Button
                                  color={
                                    deletedLineIds.includes(line.id)
                                      ? "success"
                                      : "danger"
                                  }
                                  size="sm"
                                  style={tableStyles.deleteButton}
                                  onClick={() =>
                                    handleToggleDeleteLine(line.id)
                                  }
                                >
                                  <i
                                    className={
                                      deletedLineIds.includes(line.id)
                                        ? "ri-arrow-go-back-line"
                                        : "ri-close-line"
                                    }
                                  ></i>
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="ri-error-warning-line display-5 text-danger"></i>
              <h5 className="mt-3">Détails non disponibles</h5>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {isEditing ? (
            <>
              <Button color="secondary" onClick={handleCancelEdit}>
                Annuler
              </Button>
              <Button color="primary" onClick={handleValidateChanges}>
                Valider
              </Button>
            </>
          ) : (
            <div>
              {(orderDetails?.openExpeditions || 0) > 0 &&
                !orderDetails?.date_livraison &&
                orderDetails?.array_options.options_validation == 1 && (
                  <Button
                    color="warning"
                    size="md"
                    className="me-2"
                    onClick={() => setValidationChoiceModalOpen(true)}
                  >
                    <i className="ri-check-double-line align-bottom me-1"></i>
                    Valider l'expédition
                  </Button>
                )}

              {orderPdf?.commandepdf && orderDetails?.status !== "0" && (
                <Button
                  color="info"
                  size="md"
                  className="me-2"
                  onClick={() => {
                    setPdfType("order");
                    setPdfModalOpen(true);
                  }}
                >
                  <i className="ri-file-text-line align-bottom me-2"></i> Voir
                  PDF Commande
                </Button>
              )}

              {orderPdf?.invoicepdf && orderDetails?.status !== "0" && (
                <Button
                  color="danger"
                  size="md"
                  className="me-2"
                  onClick={() => {
                    setPdfType("invoice");
                    setPdfModalOpen(true);
                  }}
                >
                  <i className="ri-file-pdf-line align-bottom me-2"></i> Voir
                  PDF Facture
                </Button>
              )}

              {(() => {
                const currentOrder = orders.find(
                  (o) => o.ref === orderDetails?.ref
                );
                const isBilled = currentOrder?.billed === "1";
                const status = orderDetails?.status;
                const isCanceled = status === "-1";
                const isStatus2 = status === "2"; // AJOUT: Vérifier le statut 2

                const shouldShowCancelDelete =
                  !isBilled && (status === "0" || status === "1");

                return (
                  <>
                    
                    {shouldShowCancelDelete && (
                      <Button
                        color="danger"
                        size="md"
                        className="me-2"
                        onClick={() => {
                          setToastMessage(
                            `Voulez-vous vraiment ${
                              status === "0" ? "supprimer" : "annuler"
                            } cette commande ?`
                          );
                          setToastColor("warning");
                          setToastOpen(true);
                          setConfirmAction(() => () => {
                            if (orderDetails) {
                              handleCancelDeleteOrder(
                                orderDetails.ref,
                                orderDetails.id
                              );
                              setModalOpen(false);
                            }
                          });
                        }}
                      >
                        <i
                          className={`ri-${
                            status === "0"
                              ? "delete-bin-line"
                              : "close-circle-line"
                          } align-bottom me-1`}
                        ></i>
                        {status === "0" ? "Supprimer" : "Annuler"}
                      </Button>
                    )}

                    {!isBilled && !isCanceled && status === "1" && (
                      <>
                        <Button
                          color="success"
                          size="md"
                          className="me-2"
                          onClick={handleAddOrderToCart}
                        >
                          <i className="ri-shopping-cart-line align-bottom me-1"></i>
                          Ajouter au panier
                        </Button>

                        <Button
                          color="primary"
                          size="md"
                          className="me-2"
                          onClick={handleEditOrder}
                        >
                          <i className="ri-edit-line align-bottom me-1"></i>{" "}
                          Modifier
                        </Button>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </ModalFooter>
      </Modal>

      {/* PDF Modal */}
      <Modal
        isOpen={pdfModalOpen}
        toggle={() => setPdfModalOpen(false)}
        size="xl"
        style={{ maxWidth: "90vw" }}
      >
        <ModalHeader toggle={() => setPdfModalOpen(false)}>
          <div>
            <i
              className={`ri-file-${
                pdfType === "invoice" ? "pdf" : "text"
              }-line me-2 ${
                pdfType === "invoice" ? "text-danger" : "text-info"
              }`}
            ></i>
            {pdfType === "invoice" ? "Facture" : "Commande"} -{" "}
            {orderDetails?.ref}
          </div>
        </ModalHeader>
        <ModalBody style={{ height: "80vh", padding: "0" }}>
          {pdfType === "invoice" ? (
            orderPdf?.invoicepdf ? (
              <iframe
                src={orderPdf.invoicepdf}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title={`Facture ${orderDetails?.ref}`}
              />
            ) : (
              <div className="text-center py-4">
                <i className="ri-error-warning-line display-5 text-muted"></i>
                <h5 className="mt-3">Facture non disponible</h5>
              </div>
            )
          ) : orderPdf?.commandepdf ? (
            <iframe
              src={orderPdf.commandepdf}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title={`Commande ${orderDetails?.ref}`}
            />
          ) : (
            <div className="text-center py-4">
              <i className="ri-error-warning-line display-5 text-muted"></i>
              <h5 className="mt-3">PDF de commande non disponible</h5>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Modal de Retour */}
      <RetourModal
        isOpen={retourModalOpen}
        toggle={() => {
          setRetourModalOpen(false);
          setRetourOrderData(null);
        }}
        user={user}
        orderData={retourOrderData}
      />

      {/* AJOUT: Modal d'Annulation/Modification de Commande */}
      <RetourAnnulationModal
        isOpen={annulationModalOpen}
        toggle={() => setAnnulationModalOpen(false)}
        user={user}
        orderData={annulationOrderData}
      />

      {toastOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ width: "400px", maxWidth: "90vw" }}>
            <Toast isOpen={toastOpen} className={`bg-white text-dark`}>
              <ToastHeader
                toggle={() => {
                  setToastOpen(false);
                  setConfirmAction(null);
                }}
                className={`bg-white text-dark d-flex justify-content-between align-items-center`}
              >
                <span>
                  <i
                    className={`me-2 ${
                      toastColor === "warning"
                        ? "ri-information-line"
                        : toastColor === "success"
                        ? "ri-check-line"
                        : "ri-error-warning-line"
                    }`}
                  ></i>
                  {toastColor === "warning"
                    ? "Confirmation"
                    : toastColor === "success"
                    ? "Succès"
                    : "Erreur"}
                </span>
              </ToastHeader>
              <ToastBody>
                <div className="d-flex flex-column">
                  <p className="mb-3 text-center">{toastMessage}</p>
                  {toastColor === "warning" && confirmAction && (
                    <div className="d-flex gap-2 justify-content-center">
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => {
                          setToastOpen(false);
                          setConfirmAction(null);
                        }}
                        className="flex-fill"
                      >
                        Annuler
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => {
                          if (confirmAction) {
                            confirmAction();
                          }
                          setToastOpen(false);
                          setConfirmAction(null);
                        }}
                        className="flex-fill"
                      >
                        Confirmer
                      </Button>
                    </div>
                  )}
                </div>
              </ToastBody>
            </Toast>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;